import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import LoginPage from "../pages/login/page";
import RegisterPage from "../pages/register/page";
import EkycPage from "../pages/ekyc/page";
import NotFoundPage from "../pages/not-found/page";
import { PrivateLayout } from "../components/feature/PrivateLayout";
import { RoleGuard } from "../components/feature/RoleGuard";

// Farmer modules
import FarmerOverviewPage from "../pages/farmer/overview/page";
import FarmerCreateProfilePage from "../pages/farmer/create-profile/page";
import FarmerInfoPage from "../pages/farmer/info/page";
import FarmerLogPage from "../pages/farmer/log/page";
import FarmerLoanPage from "../pages/farmer/loan/page";
import FarmerLimitPage from "../pages/farmer/limit/page";
import FarmerMaterialPage from "../pages/farmer/material/page";
import FarmerNotificationsPage from "../pages/farmer/notifications/page";
import FarmerSettingsPage from "../pages/farmer/settings/page";

// HTX modules
import HtxOverviewPage from "../pages/htx/overview/page";
import HtxFarmerPage from "../pages/htx/farmer/page";
import HtxHarvestPage from "../pages/htx/harvest/page";
import HtxContractPage from "../pages/htx/contract/page";
import HtxLoanPage from "../pages/htx/loan/page";
import HtxSupplyPage from "../pages/htx/supply/page";
import HtxFinancePage from "../pages/htx/finance/page";
import HtxSettingsPage from "../pages/htx/settings/page";
import HtxRegionPage from "../pages/htx/region/page";
import HtxProfilePage from "../pages/htx/profile/page";

const routes: RouteObject[] = [
  {
    path: "/dang-nhap",
    element: <LoginPage />,
  },
  {
    path: "/dang-ky",
    element: <RegisterPage />,
  },
  {
    path: "/ekyc",
    element: <EkycPage />,
  },
  // Redirect old farmer routes
  {
    path: "/nong-ho/thong-tin",
    element: <Navigate to="/nong-ho/ho-so" replace />,
  },
  // Redirect old HTX routes
  {
    path: "/htx/quan-ly-nong-ho",
    element: <Navigate to="/htx/nong-ho" replace />,
  },
  {
    path: "/htx/duyet-vay",
    element: <Navigate to="/htx/vay-han-muc" replace />,
  },
  {
    element: <PrivateLayout />,
    children: [
      // Farmer routes
      {
        path: "/nong-ho/tao-ho-so",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerCreateProfilePage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/tong-quan",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerOverviewPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/ho-so",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerInfoPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/nhat-ky",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerLogPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/vay-von",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={true}>
            <FarmerLoanPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/han-muc",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={true}>
            <FarmerLimitPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/vat-tu",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerMaterialPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/thong-bao",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerNotificationsPage />
          </RoleGuard>
        ),
      },
      {
        path: "/nong-ho/cai-dat",
        element: (
          <RoleGuard allowedRole="Nông hộ" requireEkyc={false}>
            <FarmerSettingsPage />
          </RoleGuard>
        ),
      },
      // HTX routes
      {
        path: "/htx/tong-quan",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxOverviewPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/nong-ho",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxFarmerPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/thu-hoach",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxHarvestPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/hop-dong",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxContractPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/vay-han-muc",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxLoanPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/cung-ung",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxSupplyPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/tai-chinh",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxFinancePage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/cai-dat",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxSettingsPage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/ho-so-htx",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxProfilePage />
          </RoleGuard>
        ),
      },
      {
        path: "/htx/vung-trong",
        element: (
          <RoleGuard allowedRole="Hợp tác xã">
            <HtxRegionPage />
          </RoleGuard>
        ),
      },
    ],
  },
  {
    path: "/",
    element: <LoginPage />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

export default routes;
